import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import {Sequence as BioJSSequence} from 'biojs/Sequence';
import {
  ASSEMBLY_STEP,
  CRISPRAnalysis,
  CRISPRAssemblyPrimer,
  CRISPRVector,
  FileInputStateShape,
  VECTOR_SOURCE_TYPE,
  VectorSourceType,
} from 'constants/CRISPRConstants';
import {EventConstants} from 'constants/EventConstants';
import {SequenceServerResponse} from 'constants/SequenceConstants';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';
import Enzymes, {Enzyme} from 'molbio/enzymes/backbone/Enzymes';
import EnzymeConstants from 'molbio/enzymes/EnzymeConstants';
import notifications from 'notifications';

interface CRISPRAssembly {
  name?: string;
  bases?: string;
}

export interface CRISPRAssemblyState {
  analysis: CRISPRAnalysis;
  assembledVectorIds?: Array<string>;
  assemblies: Array<CRISPRAssembly>;
  assembliesFolder: string;
  assembliesRootFolder: string;
  assemblyData: {
    vector: BioJSSequence;
    enzyme: Enzyme;
    insertionRegion: {
      start: number;
      end: number;
    };
  };
  assemblyPrimers?: Array<CRISPRAssemblyPrimer>;
  biojsSequence: BioJSSequence | null;
  enzyme: Enzyme | null;
  existingSequenceInputState: {
    sequence: SequenceServerResponse | null;
  };
  fileInputState: FileInputStateShape;
  finalizingAssemblies: boolean;
  finalizingSelectedVector: boolean;
  insertionRegion: {
    start: number | null;
    end: number | null;
  };
  step: string;
  vectors?: Array<CRISPRVector>;
  vectorsLoading?: boolean;
  vectorSelectorState: {
    vector?: CRISPRVector;
  };
  vectorSourceType: VectorSourceType;
}

class CRISPRAssemblyStore extends Store {
  vectors!: Array<CRISPRVector>;
  vectorLoadingXHRCount!: number;
  assemblyCidToAssembly!: {[cid: string]: CRISPRAssemblyState};

  override resetState(): void {
    this.vectors = [];
    this.vectorLoadingXHRCount = 0;
    this.assemblyCidToAssembly = {};
  }

  getState(): {vectors: Array<CRISPRVector>; vectorLoading: boolean} {
    return {
      vectors: this.vectors,
      vectorLoading: this.vectorLoadingXHRCount > 0,
    };
  }

  getAssemblyState(assemblyCid: string): CRISPRAssemblyState {
    return this.assemblyCidToAssembly[assemblyCid];
  }

  shouldPrependG(analysis: CRISPRAnalysis): boolean {
    return !analysis.isLeftPam;
  }

  _addVector(vector: CRISPRVector): void {
    this.vectors = reactUpdate(this.vectors, {$push: [vector]});
  }

  _updateVector(vector: CRISPRVector): void {
    this.vectors = this.vectors.map((vec) => {
      if (vec.id === vector.id) {
        return vector;
      } else {
        return vec;
      }
    });
  }

  _setVectors(vectors: Array<CRISPRVector>): void {
    this.vectors = vectors;
  }

  _selectedSequenceId(assemblyCid: string): string | undefined {
    const assemblyState = this.getAssemblyState(assemblyCid);
    switch (assemblyState.vectorSourceType) {
      case VECTOR_SOURCE_TYPE.SELECT_VECTOR:
        return assemblyState.vectorSelectorState.vector?.sequence?.id;
      case VECTOR_SOURCE_TYPE.CREATE_FROM_FILE:
        return assemblyState.fileInputState.sequence?.id;
      case VECTOR_SOURCE_TYPE.SELECT_EXISTING_SEQUENCE:
        return assemblyState.existingSequenceInputState.sequence?.id;
      default:
        return undefined;
    }
  }

  _updateAssembly(assemblyCid: string, dataToMerge: Partial<CRISPRAssemblyState>): void {
    this.assemblyCidToAssembly = reactUpdate(
      this.assemblyCidToAssembly,
      _.object([[assemblyCid, {$merge: dataToMerge}]])
    );

    if (dataToMerge.vectorSourceType != null) {
      this._fetchBiojsSequence(assemblyCid, this._selectedSequenceId(assemblyCid));
    } else if (dataToMerge.insertionRegion != null || dataToMerge.biojsSequence != null) {
      this._checkEnzyme(assemblyCid);
    }
  }

  _selectVector(assemblyCid: string, vectorId: string): void {
    const vector = _.findWhere(this.vectors, {id: vectorId});
    this._updateAssembly(assemblyCid, {
      vectorSelectorState: {vector: vector!},
      insertionRegion: {start: vector!.insertionRegionStart, end: vector!.insertionRegionEnd},
    });
    this._fetchBiojsSequence(assemblyCid, vector!.sequence.id);
  }

  _selectSequence(assemblyCid: string, sequence: SequenceServerResponse): void {
    this._updateAssembly(assemblyCid, {
      fileInputState: reactUpdate(this.getAssemblyState(assemblyCid).fileInputState, {
        $merge: {
          sequence,
          sequenceName: sequence.name,
        },
      }),
    });
    this._fetchBiojsSequence(assemblyCid, sequence.id);
  }

  _selectExistingSequence(assemblyCid: string, sequence: SequenceServerResponse): void {
    this._updateAssembly(assemblyCid, {
      existingSequenceInputState: reactUpdate(this.getAssemblyState(assemblyCid).existingSequenceInputState, {
        $merge: {sequence},
      }),
    });
    this._fetchBiojsSequence(assemblyCid, sequence != null ? sequence.id : undefined);
  }

  _fetchBiojsSequence(assemblyCid: string, sequenceId?: string): void {
    if (sequenceId == null) {
      return;
    }
    this._updateAssembly(assemblyCid, {biojsSequence: null, enzyme: null});
    const sequence = new BioJSSequence({id: sequenceId});
    sequence
      .fetch()
      .done(() => {
        AppDispatcher.dispatch({
          actionType: EventConstants.CRISPR_ASSEMBLY_FETCH_BIOJS_SEQUENCE,
          assemblyCid,
          data: {biojsSequence: sequence},
        });
      })
      .fail(notifications.notifyError);
  }

  _checkEnzyme(assemblyCid: string): void {
    let enzyme;
    const assemblyState = this.getAssemblyState(assemblyCid);
    const sequence = assemblyState.biojsSequence;
    if (sequence != null) {
      const {start, end} = assemblyState.insertionRegion;
      for (const enzymeId of EnzymeConstants.TYPE_IIS_ENZYME_IDS) {
        enzyme = Enzymes.getCommonEnzymes().get(enzymeId);
        let validStart = false;
        let validEnd = false;
        const offset = enzyme.get('offset');
        for (const cut of sequence.getCuts(enzyme.toJSON())) {
          if (offset >= 0) {
            if (start === cut.position + offset) {
              validStart = true;
            } else if (end === cut.position) {
              validEnd = true;
            }
          } else if (start === cut.position) {
            validStart = true;
          } else if (end === cut.position + offset) {
            validEnd = true;
          }
        }

        if (validEnd && validStart) {
          this._updateAssembly(assemblyCid, {enzyme});
          return;
        }
      }
    }
    this._updateAssembly(assemblyCid, {enzyme: null});
  }

  override payloadHandler(action: FluxAction): boolean {
    switch (action.actionType) {
      case EventConstants.CRISPR_VECTOR_CREATE:
      case EventConstants.CRISPR_VECTOR_GET_ALL:
      case EventConstants.CRISPR_VECTOR_UPDATE:
        this.vectorLoadingXHRCount++;
        this.emitChange();
        break;

      case EventConstants.CRISPR_VECTOR_CREATE_COMPLETED:
        this._addVector(action.vector);
        this.vectorLoadingXHRCount--;
        this.emitChange();
        break;

      case EventConstants.CRISPR_VECTOR_GET_ALL_COMPLETED:
        this._setVectors(action.vectors);
        this.vectorLoadingXHRCount--;
        this.emitChange();
        break;

      case EventConstants.CRISPR_VECTOR_UPDATE_COMPLETED:
        this._updateVector(action.vector);
        this.vectorLoadingXHRCount--;
        this.emitChange();
        break;

      case EventConstants.CRISPR_VECTOR_CREATE_FAILED:
      case EventConstants.CRISPR_VECTOR_GET_ALL_FAILED:
      case EventConstants.CRISPR_VECTOR_UPDATE_FAILED:
        this.vectorLoadingXHRCount--;
        this.emitChange();
        break;

      case EventConstants.CRISPR_ASSEMBLY_CREATE_ASSEMBLY:
        this.assemblyCidToAssembly = reactUpdate(
          this.assemblyCidToAssembly,
          _.object([[action.assemblyCid, {$set: {}}]])
        );
        this._updateAssembly(action.assemblyCid, {
          analysis: action.analysis,
          assemblies: action.assemblies,
          vectorSourceType: VECTOR_SOURCE_TYPE.SELECT_VECTOR,
          vectorSelectorState: {},
          existingSequenceInputState: {sequence: null},
          fileInputState: {
            folderId: FolderDefaults.get(FolderDefaultsKey.INVENTORY),
            importError: null,
            sequence: null,
            sequenceName: '',
          },
          insertionRegion: {start: null, end: null},
          finalizingAssemblies: false,
          finalizingSelectedVector: false,
          step: ASSEMBLY_STEP.SELECT_VECTOR,
        });
        this.emitChange();
        break;

      case EventConstants.CRISPR_ASSEMBLY_UPDATE_ASSEMBLY:
        this._updateAssembly(action.assemblyCid, action.data);
        this.emitChange();
        break;

      case EventConstants.CRISPR_ASSEMBLY_FETCH_BIOJS_SEQUENCE:
        if (action.data.biojsSequence.id === this._selectedSequenceId(action.assemblyCid)) {
          this._updateAssembly(action.assemblyCid, action.data);
          this.emitChange();
        }
        break;

      case EventConstants.CRISPR_ASSEMBLY_SELECT_VECTOR:
        this._selectVector(action.assemblyCid, action.vectorId);
        this.emitChange();
        break;

      case EventConstants.CRISPR_ASSEMBLY_SELECT_SEQUENCE:
        this._selectSequence(action.assemblyCid, action.sequence);
        this.emitChange();
        break;

      case EventConstants.CRISPR_ASSEMBLY_SELECT_EXISTING_SEQUENCE:
        this._selectExistingSequence(action.assemblyCid, action.sequence);
        this.emitChange();
        break;
      default:
        this.emitChange();
        break;
    }
    return true;
  }
}

export default new CRISPRAssemblyStore(AppDispatcher);
